import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ContentDataPDFType } from "../../types";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import styles from "./material-pdf.module.scss";
import "./content-pdf.global.scss";
import DownloadIcon from "src/assets/icons/material_download.svg?react";
import PrintIcon from "src/assets/icons/material_print.svg?react";
import ZoomInIcon from "src/assets/icons/material_zoom_in.svg?react";
import ZoomOutIcon from "src/assets/icons/material_zoom_out.svg?react";
import FullscreenIcon from "src/assets/icons/material_full.svg?react";
import FSExitIcon from "src/assets/icons/fullscreen_exit.svg?react";
import Spinner from "../spinner/Spinner";
import { downloadFile, getMediaAbsolutePath } from "../../services/File";
import Button from "../button/Button";
import FSBackIcon from "src/assets/icons/arrow_back.svg?react";
import { logger } from "../../utils/logger";
import { pdfjs } from "react-pdf";

type Props = {
  data: ContentDataPDFType;
  needPrint?: boolean;
  className?: string;
  isFullScreen: boolean;
  goBack?: () => void;
  goFullscreen?: () => void;
};

const configurePDFWorker = () => {
  try {
    // If worker is already configured, skip
    if (pdfjs.GlobalWorkerOptions.workerSrc) {
      return;
    }
    const source = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
    const script = document.createElement("script");
    script.src = source;
    script.onload = () => {
      pdfjs.GlobalWorkerOptions.workerSrc = source;
      logger.debug("[PDFJS] PDF worker loaded from:", source);
    };
    script.onerror = () => {
      logger.error("[PDFJS] Error PDF worker load");
    };
    document.head.appendChild(script);
  } catch (error) {
    logger.error("[PDFJS] Error configuring PDF worker:", error);
    pdfjs.GlobalWorkerOptions.workerSrc = "";
  }
};

// Configure worker when module loads
configurePDFWorker();

const roundScale = (num: number) => Math.round(num * 100) / 100;

function MaterialPDF(props: Props) {
  const {
    data,
    isFullScreen,
    className = "",
    needPrint,
    goBack,
    goFullscreen,
  } = props;
  const [totalPages, setTotalPages] = useState(0);
  const [pageWidth, setPageWidth] = useState<number | undefined>(undefined);
  const [scale, setScale] = useState(0.8);
  const [downloading, setDownloading] = useState(false);
  const [PDFViewerOneError, setPDFViewerOneError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const pdfWrapperRef = useRef<HTMLDivElement>(null);

  const isTablet = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/.test(
      userAgent,
    );
  }, []);

  const isPhone = useMemo(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const regexs = [
      /(Android)(.+)(Mobile)/i,
      /BlackBerry/i,
      /iPhone|iPod/i,
      /Opera Mini/i,
      /IEMobile/i,
    ];
    return regexs.some((b) => userAgent.match(b));
  }, []);
  useEffect(() => {
    if (isTablet || isPhone) {
      setScale(1);
    }
  }, [isTablet, isPhone]);
  const increaseScale = useCallback(() => {
    const newScale = Math.min(scale + 0.1, 2);
    setScale(roundScale(newScale));
  }, [scale]);

  const decreaseScale = useCallback(() => {
    const newScale = Math.max(scale - 0.1, 0.1);
    setScale(roundScale(newScale));
  }, [scale]);

  const updatePageWidth = useCallback(() => {
    const pdfClient = pdfWrapperRef.current;
    if (pdfClient) {
      const horizontalPadding = isPhone ? 16 : 60;
      setPageWidth(Math.max(pdfClient.clientWidth - horizontalPadding, 200));
    } else {
      setPageWidth(Math.max(window.innerWidth - 60, 200));
    }
  }, [isPhone]);

  useEffect(() => {
    window.addEventListener("resize", updatePageWidth);

    return () => {
      window.removeEventListener("resize", updatePageWidth);
    };
  }, [updatePageWidth]);

  useEffect(() => {
    window.addEventListener("orientationchange", updatePageWidth);

    updatePageWidth();
    return () => {
      window.removeEventListener("orientationchange", updatePageWidth);
    };
  }, [updatePageWidth]);

  const [delayedFileObj, setDelayedFileObj] = useState<string | null>(null);

  // Add a delay before loading the PDF to ensure it's fully propagated to the CDN
  useEffect(() => {
    if (data) {
      // Clear any existing file object first
      setDelayedFileObj(null);
      setPDFViewerOneError(false); // Reset error state

      // Set a delay before loading the PDF - longer for larger files
      // This helps ensure the file is fully propagated to the CDN
      const timer = setTimeout(() => {
        const path = getMediaAbsolutePath(data);
        logger.debug("[Material PDF Viewer] Loading PDF from:", path);

        // Pre-fetch the PDF to check its size
        fetch(path, { method: "HEAD" })
          .then((response) => {
            const contentLength = response.headers.get("Content-Length");
            logger.debug(
              "[Material PDF Viewer] PDF size:",
              contentLength
                ? `${Math.round(parseInt(contentLength) / 1024 / 1024)}MB`
                : "unknown",
            );

            // Set the file object to trigger loading
            setDelayedFileObj(path);
          })
          .catch((error) => {
            logger.error(
              "[Material PDF Viewer] Error pre-fetching PDF:",
              error,
            );
            // Still try to load the PDF even if pre-fetch fails
            setDelayedFileObj(path);
          });
      }, 1000); // Reduced delay to 1 second

      return () => clearTimeout(timer);
    }
  }, [data]);

  const fileObj = delayedFileObj;

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }) => {
      logger.info(
        `[Material PDF Viewer] PDF loaded successfully with ${numPages} pages`,
      );
      if (needPrint) {
        printFile();
      }
      setTotalPages(numPages);
      setTimeout(updatePageWidth, 0);
    },
    [needPrint, updatePageWidth],
  );

  const pages = useMemo(() => {
    if (fileObj && totalPages && pageWidth) {
      return Array.from(new Array(totalPages), (el, index) => {
        return (
          <div key={`page_${index + 1}`}>
            <Page
              width={pageWidth}
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              loading={""}
              scale={scale}
            />
            {index < totalPages - 1 && <hr className={styles.pageDivider} />}
          </div>
        );
      });
    }
  }, [fileObj, totalPages, pageWidth, scale]);

  const download = useCallback(() => {
    if (data) {
      setDownloading(true);
      downloadFile(data, "pdf")
        .catch(() => {
          alert(`Can't download file.`);
        })
        .finally(() => {
          setDownloading(false);
        });
    }
  }, [data]);

  const printFile = () => {
    let getPrintEle = document.getElementById("hiddenFileHackForPrint");

    setTimeout(() => {
      if (getPrintEle?.getAttribute("src") === null) {
        throw new Error("PDF not loaded");
      }
      (getPrintEle as HTMLIFrameElement)?.contentWindow?.print();
    }, 500);
  };

  useEffect(() => {
    if (fileObj) {
      logger.debug(
        "[Material PDF Viewer] Fetching PDF for print functionality",
      );

      // Use a timeout to avoid blocking the main thread
      const fetchTimer = setTimeout(() => {
        fetch(fileObj)
          .then((resp) => {
            if (!resp.ok) {
              throw new Error(`HTTP error! Status: ${resp.status}`);
            }
            return resp.blob();
          })
          .then((blob) => {
            logger.debug(
              "[Material PDF Viewer] PDF fetched successfully, size:",
              `${Math.round(blob.size / 1024 / 1024)}MB`,
            );
            const newPDFBlob = new Blob([blob], { type: "application/pdf" });
            const localUrl = window.URL.createObjectURL(newPDFBlob);
            const iframeEle = document.getElementById("hiddenFileHackForPrint");
            if (iframeEle) {
              // @ts-ignore
              iframeEle.src = localUrl;
            }
            window.URL.revokeObjectURL(localUrl);
          })
          .catch((e) => {
            logger.error("[Material PDF Viewer] Error fetching PDF:", e);
          });
      }, 0);

      return () => clearTimeout(fetchTimer);
    }
  }, [fileObj]);

  const handleFullscreen = () => {
    goFullscreen?.();
  };

  if (!fileObj) return null;

  return (
    <div
      id="pdfWrapper"
      ref={pdfWrapperRef}
      className={[
        styles.wrapper,
        isPhone ? styles.wrapperMobile : "",
        className,
      ].join(" ")}
    >
      <iframe
        id="hiddenFileHackForPrint"
        title="hiddenFileHackForPrint"
        style={{ display: "none" }}
      />
      <div
        className={[styles.topBar, isPhone ? styles.topBarMobile : ""].join(
          " ",
        )}
      >
        <div className={styles.menuBar}>
          <button
            onClick={goBack}
            className={styles.closeButton}
            type={"button"}
            aria-label="Back to lesson"
          >
            <div className={styles.menuWrapper}>
              <FSBackIcon className={styles.closeIcon} />
            </div>
          </button>
        </div>
        <div className={styles.toolbar}>
          {!isPhone && (
            <div
              className={styles.backTipView}
              style={{ display: isHovered ? "flex" : "none" }}
            >
              <div className={styles.arrowUp}></div>
              <div className={styles.tipTextView}>
                <div className={styles.tipTitle}>Download PDF</div>
              </div>
            </div>
          )}
          <div
            onMouseOver={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Button
              label=""
              disabled={downloading}
              onClick={download}
              className={styles.toolbarBtn}
              leftIcon={<DownloadIcon />}
            />
          </div>
          {!PDFViewerOneError && (
            <>
              {!isPhone && (
                <Button
                  label=""
                  disabled={downloading}
                  onClick={printFile}
                  className={styles.toolbarBtn}
                  leftIcon={<PrintIcon />}
                />
              )}
              {!isPhone && (
                <Button
                  label=""
                  className={styles.toolbarBtn}
                  onClick={handleFullscreen}
                  leftIcon={
                    isFullScreen ? (
                      <FSExitIcon fill="#3C595C" />
                    ) : (
                      <FullscreenIcon />
                    )
                  }
                />
              )}
              <Button
                label=""
                className={styles.toolbarBtn}
                onClick={decreaseScale}
                leftIcon={<ZoomOutIcon />}
              />
              <Button
                label=""
                className={styles.toolbarBtn}
                onClick={increaseScale}
                leftIcon={<ZoomInIcon />}
              />
              <div className={styles.scaleTitle}>
                {Number(scale * 100).toFixed(0)}%
              </div>
            </>
          )}
        </div>
      </div>
      {!PDFViewerOneError ? (
        <div className={styles.content}>
          <Document
            onLoadError={(error) => {
              logger.error("[Material PDF Viewer] Error loading PDF:", error);
              setPDFViewerOneError(true);
            }}
            file={fileObj}
            onLoadSuccess={onDocumentLoadSuccess}
            className={styles.document}
            loading={
              <div className={styles.loadingContainer}>
                <Spinner />
                <div className={styles.loadingText}>Loading PDF...</div>
                <div className={styles.loadingSubtext}>
                  Large files may take longer to load
                </div>
              </div>
            }
            externalLinkTarget={"_blank"}
          >
            {pages}
          </Document>
        </div>
      ) : (
        <div className={styles.contentTwo}>
          <div style={{ padding: "20px", textAlign: "center" }}>
            <div style={{ marginBottom: 20 }}>
              Sorry, we are having troubles displaying your PDF. Please contact
              support@openparachuteschools.com to let us know. In the meantime,
              please click here to download the file:
            </div>
            <Button
              className={styles.emergencyButton}
              label={"Download your PDF"}
              onClick={download}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default MaterialPDF;
